import { deDE } from '@clerk/localizations';
import { ClerkProvider } from '@clerk/react-router';
import type { ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { CatalogProvider } from '../../features/catalog/components/global-catalog-provider.tsx';
import { CustomerModeProvider } from '../../features/customer-mode/hooks/use-customer-mode.tsx';
import { CLERK_PUBLISHABLE_KEY } from '../../lib/constants.ts';
import { VoiceRecordingProvider } from '../../lib/contexts/voice-recording-context.tsx';
import { i18n } from '../../lib/i18n/i18n.ts';
import { Toaster } from '../ui/toaster.tsx';
import { TooltipProvider } from '../ui/tooltip.tsx';
import { AppStateMachineProvider } from './app-state-machine-provider.tsx';
import { AppStateRedirectProvider } from './app-state-redirect-provider.tsx';
import { IntercomProvider } from './intercom-provider.tsx';
import { PosthogProvider } from './posthog-provider.tsx';
import { SentryProvider } from './sentry-provider.tsx';
import { TRPCProvider } from './trpc-provider.tsx';

if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
}

type AppProvidersProps = {
    locale?: string;
    children?: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps) {
    return (
        <HelmetProvider>
            <TRPCProvider>
                <ClerkProvider
                    publishableKey={CLERK_PUBLISHABLE_KEY}
                    appearance={{
                        variables: {
                            colorPrimary: '#103FEF',
                        },
                        elements: {
                            footer: 'hidden',
                        },
                    }}
                    localization={i18n.language === 'de' ? deDE : undefined}
                    signInUrl="/sign-in"
                >
                    <I18nextProvider i18n={i18n}>
                        <AppStateMachineProvider>
                            <AppStateRedirectProvider>
                                <PosthogProvider>
                                    <IntercomProvider>
                                        <SentryProvider>
                                            <CustomerModeProvider>
                                                <VoiceRecordingProvider>
                                                    <TooltipProvider>
                                                        <CatalogProvider>{children}</CatalogProvider>
                                                        <Toaster />
                                                    </TooltipProvider>
                                                </VoiceRecordingProvider>
                                            </CustomerModeProvider>
                                        </SentryProvider>
                                    </IntercomProvider>
                                </PosthogProvider>
                            </AppStateRedirectProvider>
                        </AppStateMachineProvider>
                    </I18nextProvider>
                </ClerkProvider>
            </TRPCProvider>
        </HelmetProvider>
    );
}
